/**
 * TUVI - Carrito de Compras General
 *
 * Todo lo que hay que comprar de las cotizaciones que están en la Ficha FFE:
 * actividades con Req. Compra = SI en la matriz, agrupadas por el proveedor de la
 * matriz (las que no tienen van en "Sin proveedor asignado").
 * Muestra lo que sigue en Ficha FFE y lo que pasó a ejecución desde octubre 2026.
 * "Hacer compra" (por proveedor) registra la compra con su factura; en la pestaña
 * Compras se gestiona hasta que llega y pasa al presupuesto del proyecto.
 * Lo pendiente descuenta lo ya comprado; la marca manual sigue disponible.
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import * as XLSX from 'xlsx';
import client from '../../services/graphqlClient';
import { useNotification } from '../../context/NotificationContext';
import ModalHacerCompra from './ModalHacerCompra';
import ComprasRegistradas from './ComprasRegistradas';
import './CarritoComprasGeneral.css';

const SIN = 'Sin proveedor asignado';
const AVANCE = {
  por_agendar: 'Por agendar', agendado: 'Agendado', en_elaboracion: 'En elaboración',
  en_revision: 'En revisión del cliente', firmado_aprobado: 'Firmado y aprobado',
};
const QUERY = `query { carritoComprasGeneral { id cotizacion_id source codigo descripcion unidad cantidad espacio
  proveedor_id proveedor comprado comprado_manual comprado_at comprado_por notas cantidad_comprada cantidad_recibida
  cantidad_pendiente compras_ids consecutivo nombre_cliente proyecto_nombre apartamento avance_ffe enviado_proyecto
  proyecto_id fecha_ejecucion } }`;

const fecha = (iso) => (iso ? new Date(iso).toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' }) : '');
const fechaHora = (iso) => (iso ? new Date(iso).toLocaleString('es-CO', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '');
const num = (v) => Number(v || 0).toLocaleString('es-CO', { maximumFractionDigits: 2 });
const proyectoApto = (l) => `${l.proyecto_nombre || ''}${l.apartamento ? ' ' + l.apartamento : ''}`.trim();
const nombreProyecto = (l) => `${proyectoApto(l)} · COT-${l.consecutivo ?? ''}`;
const contiene = (texto, q) => String(texto || '').toLowerCase().includes(q.trim().toLowerCase());

// Filtro donde se puede escribir o pegar: sugiere opciones y filtra por lo que contenga el texto
function FiltroTexto({ id, valor, onChange, opciones, placeholder }) {
  return (
    <div className="ccg-combo">
      <input type="text" list={id} value={valor} placeholder={placeholder}
        onChange={e => onChange(e.target.value)} />
      {valor && <button type="button" className="ccg-combo-x" title="Limpiar" onClick={() => onChange('')}>×</button>}
      <datalist id={id}>{opciones.map(o => <option key={o} value={o} />)}</datalist>
    </div>
  );
}
const claveCot = (l) => `${l.source}:${l.cotizacion_id}`;
const pendiente = (l) => (l.comprado ? 0 : Number(l.cantidad_pendiente ?? l.cantidad ?? 0));
const hoyMenos = (dias) => { const d = new Date(); d.setDate(d.getDate() - dias); return d.toISOString().split('T')[0]; };

export default function CarritoComprasGeneral() {
  const { success, error } = useNotification();
  const [tab, setTab] = useState('proveedor');
  const [lineas, setLineas] = useState(null);
  const [filtro, setFiltro] = useState({ buscar: '', proveedor: '', proyecto: '', estado: 'pendientes', enviados: 'todos' });
  const [vista, setVista] = useState('consolidado'); // detalle | consolidado (pestaña proveedor)
  const [modalCompra, setModalCompra] = useState(null); // { titulo, lineas }
  const [compraNueva, setCompraNueva] = useState(null);
  const [abiertos, setAbiertos] = useState({});
  const [guardando, setGuardando] = useState(false);

  // Historial
  const [historial, setHistorial] = useState(null);
  const [rango, setRango] = useState({ desde: hoyMenos(30), hasta: new Date().toISOString().split('T')[0] });
  const [verAnuladas, setVerAnuladas] = useState(false);
  const [buscarHist, setBuscarHist] = useState('');

  const cargar = useCallback(async () => {
    try {
      const d = await client.query(QUERY);
      setLineas(d.carritoComprasGeneral || []);
    } catch (e) {
      console.error('[Carrito General]', e);
      setLineas([]);
      error(`Error cargando el carrito de compras: ${e.message || e}`);
    }
  }, [error]);

  const cargarHistorial = useCallback(async () => {
    setHistorial(null);
    try {
      const d = await client.query(`query($d: String, $h: String) { carritoComprasHistorial(desde: $d, hasta: $h) {
        id cotizacion_id source linea_id consecutivo nombre_cliente proyecto_nombre apartamento codigo descripcion
        cantidad unidad proveedor notas comprado_por comprado_at anulado_at anulado_por } }`, { d: rango.desde || null, h: rango.hasta || null });
      setHistorial(d.carritoComprasHistorial || []);
    } catch (e) {
      console.error('[Carrito General - historial]', e);
      setHistorial([]);
      error(`Error cargando el historial: ${e.message || e}`);
    }
  }, [rango, error]);

  useEffect(() => { cargar(); }, [cargar]);
  useEffect(() => { if (tab === 'historial') cargarHistorial(); }, [tab, cargarHistorial]);

  // ===== Marcar / anular compras =====
  const marcar = async (items, comprado, conNota = false) => {
    if (!items.length) return;
    let notas = null;
    if (comprado && conNota) {
      notas = window.prompt(`Registrar ${items.length} compra(s). Nota opcional (factura, orden, observación):`, '');
      if (notas === null) return;
    }
    if (!comprado && items.length > 1 && !window.confirm(`¿Desmarcar ${items.length} compras? Quedarán anuladas en el historial.`)) return;
    const antes = lineas;
    const ids = new Set(items.map(l => `${l.source}:${l.id}`));
    setLineas(prev => prev.map(l => ids.has(`${l.source}:${l.id}`) ? { ...l, comprado } : l));
    setGuardando(true);
    try {
      const r = await client.mutate(`mutation($items: [CompraCarritoInput!]!, $c: Boolean!, $n: String) { marcarComprasCarrito(items: $items, comprado: $c, notas: $n) { success message } }`,
        { items: items.map(l => ({ cotizacion_id: l.cotizacion_id, source: l.source, linea_id: l.id })), c: comprado, n: notas || null });
      if (r.marcarComprasCarrito.success) { success(r.marcarComprasCarrito.message); cargar(); }
      else { setLineas(antes); error(r.marcarComprasCarrito.message); }
    } catch (e) { setLineas(antes); error('No se pudo guardar'); }
    finally { setGuardando(false); }
  };

  // ===== Filtros =====
  const proveedores = useMemo(() => {
    const s = new Set((lineas || []).map(l => l.proveedor || SIN));
    return [...s].sort((a, b) => (a === SIN) - (b === SIN) || a.localeCompare(b, 'es'));
  }, [lineas]);
  const proyectos = useMemo(() => {
    const s = new Set((lineas || []).map(nombreProyecto));
    return [...s].sort((a, b) => a.localeCompare(b, 'es'));
  }, [lineas]);

  const filtradas = useMemo(() => {
    const q = filtro.buscar.trim().toLowerCase();
    return (lineas || []).filter(l => {
      if (filtro.estado === 'pendientes' && l.comprado) return false;
      if (filtro.estado === 'comprados' && !l.comprado) return false;
      if (filtro.proveedor.trim() && !contiene(l.proveedor || SIN, filtro.proveedor)) return false;
      if (filtro.proyecto.trim() && !contiene(`${nombreProyecto(l)} ${l.nombre_cliente || ''}`, filtro.proyecto)) return false;
      if (filtro.enviados === 'no' && l.enviado_proyecto) return false;
      if (filtro.enviados === 'si' && !l.enviado_proyecto) return false;
      if (q) {
        const t = `${l.codigo} ${l.descripcion} ${l.nombre_cliente} ${l.proyecto_nombre} ${l.apartamento} ${l.consecutivo}`.toLowerCase();
        if (!t.includes(q)) return false;
      }
      return true;
    });
  }, [lineas, filtro]);

  const agrupar = (lista, claveFn, ordenFn) => {
    const g = new Map();
    lista.forEach(l => { const k = claveFn(l); if (!g.has(k)) g.set(k, []); g.get(k).push(l); });
    return [...g.entries()].sort(ordenFn);
  };
  const porProveedor = useMemo(() => agrupar(filtradas, l => l.proveedor || SIN,
    (a, b) => (a[0] === SIN) - (b[0] === SIN) || a[0].localeCompare(b[0], 'es')), [filtradas]);
  const porProyecto = useMemo(() => agrupar(filtradas, claveCot,
    (a, b) => (a[1][0].consecutivo || 0) - (b[1][0].consecutivo || 0)), [filtradas]);

  // Consolidado: misma actividad (código + unidad) sumada entre proyectos
  const consolidar = (items) => {
    const m = new Map();
    items.forEach(l => {
      const k = `${l.codigo}|${l.unidad}`;
      if (!m.has(k)) m.set(k, { codigo: l.codigo, descripcion: l.descripcion, unidad: l.unidad, cantidad: 0, pendiente: 0, lineas: [] });
      const c = m.get(k); c.cantidad += Number(l.cantidad || 0); c.pendiente += pendiente(l); c.lineas.push(l);
    });
    return [...m.values()].sort((a, b) => String(a.codigo).localeCompare(String(b.codigo)));
  };

  // ===== Resumen =====
  const resumen = useMemo(() => {
    const todas = lineas || [];
    const pend = todas.filter(l => !l.comprado);
    return {
      pendientes: pend.length,
      comprados: todas.length - pend.length,
      sinProveedor: pend.filter(l => !l.proveedor).length,
      proyectos: new Set(pend.map(claveCot)).size,
      proveedores: new Set(pend.filter(l => l.proveedor).map(l => l.proveedor)).size,
    };
  }, [lineas]);

  // ===== Excel =====
  const exportar = () => {
    const filas = filtradas.map(l => ({
      Proveedor: l.proveedor || SIN, Cotización: `COT-${l.consecutivo ?? ''}`, Cliente: l.nombre_cliente || '',
      Proyecto: l.proyecto_nombre || '', Apto: l.apartamento || '', Código: l.codigo, Descripción: l.descripcion,
      Espacio: l.espacio || '', Cantidad: Number(l.cantidad || 0), Comprada: Number(l.cantidad_comprada || 0), Pendiente: pendiente(l), Unidad: l.unidad,
      Estado: l.comprado ? 'Comprado' : Number(l.cantidad_comprada) > 0 ? 'Comprado parcial' : 'Pendiente', 'Comprado por': l.comprado_por || '', 'Fecha compra': fecha(l.comprado_at),
      Nota: l.notas || '',
    })).sort((a, b) => (a.Proveedor === SIN) - (b.Proveedor === SIN) || a.Proveedor.localeCompare(b.Proveedor, 'es'));
    const cons = [];
    porProveedor.forEach(([prov, items]) => consolidar(items).forEach(c => cons.push({
      Proveedor: prov, Código: c.codigo, Descripción: c.descripcion, 'Cantidad total': c.cantidad, 'Cantidad pendiente': c.pendiente, Unidad: c.unidad,
      Proyectos: new Set(c.lineas.map(claveCot)).size, Pendientes: c.lineas.filter(l => !l.comprado).length,
    })));
    const wb = XLSX.utils.book_new();
    const ws1 = XLSX.utils.json_to_sheet(filas);
    ws1['!cols'] = [28, 11, 26, 24, 8, 14, 50, 16, 9, 10, 10, 7, 15, 18, 13, 30].map(w => ({ wch: w }));
    XLSX.utils.book_append_sheet(wb, ws1, 'Detalle');
    const ws2 = XLSX.utils.json_to_sheet(cons);
    ws2['!cols'] = [28, 14, 50, 13, 7, 10, 11].map(w => ({ wch: w }));
    XLSX.utils.book_append_sheet(wb, ws2, 'Consolidado por proveedor');
    XLSX.writeFile(wb, `Carrito_Compras_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const exportarHistorial = () => {
    const filas = historialFiltrado().map(h => ({
      'Fecha compra': fechaHora(h.comprado_at), Proveedor: h.proveedor || SIN, Cotización: `COT-${h.consecutivo ?? ''}`,
      Cliente: h.nombre_cliente || '', Proyecto: h.proyecto_nombre || '', Apto: h.apartamento || '',
      Código: h.codigo, Descripción: h.descripcion, Cantidad: Number(h.cantidad || 0), Unidad: h.unidad,
      'Comprado por': h.comprado_por || '', Nota: h.notas || '',
      Estado: h.anulado_at ? `Anulada (${h.anulado_por || ''} ${fecha(h.anulado_at)})` : 'Vigente',
    }));
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(filas);
    ws['!cols'] = [18, 28, 11, 26, 24, 8, 14, 50, 9, 7, 18, 30, 28].map(w => ({ wch: w }));
    XLSX.utils.book_append_sheet(wb, ws, 'Historial');
    XLSX.writeFile(wb, `Historial_Compras_${rango.desde}_${rango.hasta}.xlsx`);
  };

  const toggleGrupo = (k) => setAbiertos(p => ({ ...p, [k]: !p[k] }));
  const abierto = (k) => !!abiertos[k];

  // ===== Render helpers =====
  const porCompra = (l) => l.comprado && !l.comprado_manual;
  const checkLinea = (l) => (
    <input type="checkbox" className="ccg-check" checked={!!l.comprado} disabled={guardando || porCompra(l)}
      title={porCompra(l) ? 'Comprado en una compra registrada (pestaña Compras)' : l.comprado ? 'Desmarcar (anular la marca manual)' : 'Marcar como comprado sin registrar compra'}
      onChange={() => marcar([l], !l.comprado)} />
  );
  const infoCompra = (l) => (
    <>
      {l.comprado_manual && <div className="ccg-comprado-info">✓ Marcado por {l.comprado_por}{l.comprado_at ? ` · ${fecha(l.comprado_at)}` : ''}{l.notas ? ` · ${l.notas}` : ''}</div>}
      {Number(l.cantidad_comprada) > 0 && (
        <div className="ccg-comprado-info">
          🛒 Compra{l.compras_ids?.length > 1 ? 's' : ''} {(l.compras_ids || []).map(id => `#${id}`).join(', ')} · {num(l.cantidad_comprada)} de {num(l.cantidad)} {l.unidad}
          {Number(l.cantidad_recibida) > 0 ? ` · llegó ${num(l.cantidad_recibida)}` : ''}
        </div>
      )}
    </>
  );
  const abrirCompra = (titulo, items) => {
    const pend = items.filter(l => !l.comprado && pendiente(l) > 0);
    if (!pend.length) return;
    setModalCompra({ titulo, lineas: pend });
  };
  const cabeceraGrupo = (k, titulo, items, extra, esSin, conCompra = false) => {
    const pend = items.filter(l => !l.comprado);
    return (
      <div className={`ccg-grupo-header ${esSin ? 'sin-proveedor' : ''}`} onClick={() => toggleGrupo(k)}>
        <span className="ccg-flecha">{abierto(k) ? '▾' : '▸'}</span>
        <span className="ccg-grupo-titulo">{titulo}</span>
        {conCompra && pend.length > 0 && (
          <button className="ccg-btn-compra" onClick={(e) => { e.stopPropagation(); abrirCompra(titulo, items); }}>
            🛒 Hacer compra
          </button>
        )}
        {extra}
        <span className="ccg-pill">{items.length - pend.length}/{items.length} comprados</span>
        {pend.length > 0 && (
          <button className="ccg-btn-mini sec" disabled={guardando} title="Marcar como comprado sin registrar una compra"
            onClick={(e) => { e.stopPropagation(); marcar(pend, true, true); }}>
            Marcar {pend.length} comprado{pend.length === 1 ? '' : 's'}
          </button>
        )}
      </div>
    );
  };

  const renderPorProveedor = () => porProveedor.map(([prov, items]) => {
    const k = `prov:${prov}`;
    const nProy = new Set(items.map(claveCot)).size;
    return (
      <div key={k} className={`ccg-grupo ${prov === SIN ? 'sin-proveedor' : ''}`}>
        {cabeceraGrupo(k, prov, items, <span className="ccg-sub">{nProy} proyecto{nProy === 1 ? '' : 's'}</span>, prov === SIN, true)}
        {abierto(k) && (vista === 'detalle' ? (
          <table className="ccg-table">
            <thead><tr><th></th><th>Proyecto</th><th>Código</th><th>Descripción</th><th>Espacio</th><th>Pendiente</th><th>Und</th></tr></thead>
            <tbody>
              {items.map(l => (
                <tr key={`${l.source}:${l.id}`} className={l.comprado ? 'ccg-hecho' : ''}>
                  <td>{checkLinea(l)}</td>
                  <td className="ccg-proyecto">
                    <strong>COT-{l.consecutivo}</strong> {l.proyecto_nombre} {l.apartamento}
                    <div className="ccg-cliente">{l.nombre_cliente}</div>
                  </td>
                  <td className="ccg-codigo">{l.codigo}</td>
                  <td>{l.descripcion}{infoCompra(l)}</td>
                  <td>{l.espacio}</td>
                  <td className="ccg-num">{num(pendiente(l))}{pendiente(l) !== Number(l.cantidad) ? <div className="ccg-cliente">de {num(l.cantidad)}</div> : null}</td>
                  <td>{l.unidad}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <table className="ccg-table">
            <thead><tr><th></th><th>Código</th><th>Descripción</th><th>Pendiente</th><th>Und</th><th>Proyectos</th></tr></thead>
            <tbody>
              {consolidar(items).map(c => {
                const pend = c.lineas.filter(l => !l.comprado);
                const todo = pend.length === 0;
                return (
                  <tr key={`${c.codigo}|${c.unidad}`} className={todo ? 'ccg-hecho' : ''}>
                    <td>
                      <input type="checkbox" className="ccg-check" checked={todo} disabled={guardando}
                        title={todo ? 'Desmarcar todas' : 'Marcar todas como compradas'}
                        onChange={() => (todo ? marcar(c.lineas, false) : marcar(pend, true, true))} />
                    </td>
                    <td className="ccg-codigo">{c.codigo}</td>
                    <td>{c.descripcion}</td>
                    <td className="ccg-num"><strong>{num(c.pendiente)}</strong>{c.pendiente !== c.cantidad ? <div className="ccg-cliente">de {num(c.cantidad)}</div> : null}</td>
                    <td>{c.unidad}</td>
                    <td className="ccg-proyectos-lista">
                      {[...c.lineas].sort((a, b) => proyectoApto(a).localeCompare(proyectoApto(b), 'es', { numeric: true })).map(l => (
                        <span key={`${l.source}:${l.id}`} className={`ccg-chip ${l.comprado ? 'hecho' : ''}`}
                          title={`COT-${l.consecutivo} · ${l.nombre_cliente || ''}${l.comprado ? ' · Comprado' : Number(l.cantidad_comprada) > 0 ? ` · comprado ${num(l.cantidad_comprada)} de ${num(l.cantidad)}` : ''}`}>
                          {proyectoApto(l) || `COT-${l.consecutivo}`} <strong>({num(l.comprado ? l.cantidad : pendiente(l))})</strong>
                        </span>
                      ))}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ))}
      </div>
    );
  });

  const renderPorProyecto = () => porProyecto.map(([k, items]) => {
    const l0 = items[0];
    const gk = `proy:${k}`;
    return (
      <div key={gk} className="ccg-grupo">
        {cabeceraGrupo(gk, nombreProyecto(l0), items, (
          <span className="ccg-sub">
            {l0.nombre_cliente} · {AVANCE[l0.avance_ffe] || l0.avance_ffe}
            {l0.enviado_proyecto && <span className="ccg-tag-ejec">En ejecución</span>}
          </span>
        ))}
        {abierto(gk) && (
          <table className="ccg-table">
            <thead><tr><th></th><th>Proveedor</th><th>Código</th><th>Descripción</th><th>Espacio</th><th>Pendiente</th><th>Und</th></tr></thead>
            <tbody>
              {[...items].sort((a, b) => (a.proveedor ? 0 : 1) - (b.proveedor ? 0 : 1) || String(a.proveedor).localeCompare(String(b.proveedor), 'es')).map(l => (
                <tr key={`${l.source}:${l.id}`} className={l.comprado ? 'ccg-hecho' : ''}>
                  <td>{checkLinea(l)}</td>
                  <td>{l.proveedor ? <strong>{l.proveedor}</strong> : <span className="ccg-sin">{SIN}</span>}</td>
                  <td className="ccg-codigo">{l.codigo}</td>
                  <td>{l.descripcion}{infoCompra(l)}</td>
                  <td>{l.espacio}</td>
                  <td className="ccg-num">{num(pendiente(l))}{pendiente(l) !== Number(l.cantidad) ? <div className="ccg-cliente">de {num(l.cantidad)}</div> : null}</td>
                  <td>{l.unidad}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    );
  });

  const historialFiltrado = () => (historial || []).filter(h => (verAnuladas || !h.anulado_at) && (!buscarHist.trim() ||
    contiene(`${h.codigo} ${h.descripcion} ${h.proveedor || SIN} ${h.proyecto_nombre} ${h.apartamento} ${h.nombre_cliente} COT-${h.consecutivo} ${h.comprado_por} ${h.notas || ''}`, buscarHist)));

  const renderHistorial = () => {
    const filas = historialFiltrado();
    return (
      <>
        <div className="ccg-filtros">
          <input type="text" className="ccg-buscar" placeholder="Buscar código, actividad, proveedor, proyecto, quién compró..."
            value={buscarHist} onChange={e => setBuscarHist(e.target.value)} />
          <label>Desde <input type="date" value={rango.desde} onChange={e => setRango({ ...rango, desde: e.target.value })} /></label>
          <label>Hasta <input type="date" value={rango.hasta} onChange={e => setRango({ ...rango, hasta: e.target.value })} /></label>
          <label className="ccg-check-label"><input type="checkbox" checked={verAnuladas} onChange={e => setVerAnuladas(e.target.checked)} /> Mostrar anuladas</label>
          <span className="ccg-spacer" />
          <button className="ccg-btn-sec" onClick={exportarHistorial} disabled={!filas.length}>Exportar Excel</button>
        </div>
        {historial === null ? <p className="ccg-vacio">Cargando...</p> : filas.length === 0 ? (
          <p className="ccg-vacio">No hay compras registradas en ese rango.</p>
        ) : (
          <div className="ccg-grupo">
            <table className="ccg-table">
              <thead><tr><th>Fecha</th><th>Proveedor</th><th>Proyecto</th><th>Código</th><th>Descripción</th><th>Cant</th><th>Comprado por</th><th>Nota</th><th>Estado</th></tr></thead>
              <tbody>
                {filas.map(h => (
                  <tr key={h.id} className={h.anulado_at ? 'ccg-anulada' : ''}>
                    <td className="ccg-nowrap">{fechaHora(h.comprado_at)}</td>
                    <td>{h.proveedor || <span className="ccg-sin">{SIN}</span>}</td>
                    <td className="ccg-proyecto"><strong>COT-{h.consecutivo}</strong> {h.proyecto_nombre} {h.apartamento}<div className="ccg-cliente">{h.nombre_cliente}</div></td>
                    <td className="ccg-codigo">{h.codigo}</td>
                    <td>{h.descripcion}</td>
                    <td className="ccg-num ccg-nowrap">{num(h.cantidad)} {h.unidad}</td>
                    <td>{h.comprado_por}</td>
                    <td>{h.notas}</td>
                    <td className="ccg-nowrap">{h.anulado_at ? <span className="ccg-tag-anulada" title={`Anulada por ${h.anulado_por || ''}`}>Anulada {fecha(h.anulado_at)}</span> : <span className="ccg-tag-ok">Vigente</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </>
    );
  };

  return (
    <div className="ccg">
      <div className="ccg-header">
        <div>
          <h1>Carrito de Compras General</h1>
          <p>Actividades con Req. Compra = SI en la matriz, por el proveedor de la matriz: proyectos que siguen en Ficha FFE y los que pasaron a ejecución desde octubre de 2026.</p>
        </div>
        <button className="ccg-btn-sec" onClick={cargar}>Actualizar</button>
      </div>

      <div className="ccg-cards">
        <div className="ccg-card"><span>Pendientes por comprar</span><strong>{resumen.pendientes}</strong></div>
        <div className="ccg-card ok"><span>Compradas</span><strong>{resumen.comprados}</strong></div>
        <div className="ccg-card"><span>Proyectos con pendientes</span><strong>{resumen.proyectos}</strong></div>
        <div className="ccg-card"><span>Proveedores con pendientes</span><strong>{resumen.proveedores}</strong></div>
        <div className={`ccg-card ${resumen.sinProveedor ? 'alerta' : ''}`}><span>Pendientes sin proveedor</span><strong>{resumen.sinProveedor}</strong></div>
      </div>

      <div className="ccg-tabs">
        <button className={tab === 'proveedor' ? 'active' : ''} onClick={() => setTab('proveedor')}>Por proveedor</button>
        <button className={tab === 'proyecto' ? 'active' : ''} onClick={() => setTab('proyecto')}>Por proyecto</button>
        <button className={tab === 'compras' ? 'active' : ''} onClick={() => setTab('compras')}>Compras</button>
        <button className={tab === 'historial' ? 'active' : ''} onClick={() => setTab('historial')}>Marcas manuales</button>
      </div>

      {modalCompra && (
        <ModalHacerCompra titulo={modalCompra.titulo} lineas={modalCompra.lineas} onClose={() => setModalCompra(null)}
          onGuardado={(id) => { setModalCompra(null); setCompraNueva(id); cargar(); setTab('compras'); }} />
      )}

      {tab === 'compras' ? <ComprasRegistradas onCambio={cargar} compraResaltada={compraNueva} /> : tab === 'historial' ? renderHistorial() : (
        <>
          <div className="ccg-filtros">
            <input type="text" placeholder="Buscar código, actividad, cliente, proyecto..." value={filtro.buscar}
              onChange={e => setFiltro({ ...filtro, buscar: e.target.value })} className="ccg-buscar" />
            <select value={filtro.estado} onChange={e => setFiltro({ ...filtro, estado: e.target.value })}>
              <option value="pendientes">Pendientes</option>
              <option value="comprados">Comprados</option>
              <option value="todos">Todos</option>
            </select>
            <FiltroTexto id="ccg-proveedores" valor={filtro.proveedor} opciones={proveedores}
              placeholder="Proveedor (escribe o pega)" onChange={v => setFiltro({ ...filtro, proveedor: v })} />
            <FiltroTexto id="ccg-proyectos" valor={filtro.proyecto} opciones={proyectos}
              placeholder="Proyecto (escribe o pega)" onChange={v => setFiltro({ ...filtro, proyecto: v })} />
            <select value={filtro.enviados} onChange={e => setFiltro({ ...filtro, enviados: e.target.value })}>
              <option value="todos">FFE: todos</option>
              <option value="no">Aún en Ficha FFE</option>
              <option value="si">Ya enviados a ejecución</option>
            </select>
            {tab === 'proveedor' && (
              <div className="ccg-vista">
                <button className={vista === 'detalle' ? 'active' : ''} onClick={() => setVista('detalle')}>Detalle</button>
                <button className={vista === 'consolidado' ? 'active' : ''} onClick={() => setVista('consolidado')}>Consolidado</button>
              </div>
            )}
            <span className="ccg-spacer" />
            <button className="ccg-btn-sec" onClick={exportar} disabled={!filtradas.length}>Exportar Excel</button>
          </div>

          {lineas === null ? <p className="ccg-vacio">Cargando...</p> : filtradas.length === 0 ? (
            <p className="ccg-vacio">{filtro.estado === 'pendientes' ? 'No hay compras pendientes con estos filtros.' : 'No hay actividades con estos filtros.'}</p>
          ) : (tab === 'proveedor' ? renderPorProveedor() : renderPorProyecto())}
        </>
      )}
    </div>
  );
}
